import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Scale, ReceiptText, Gauge, ShieldCheck, FileText, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { ClientOutcomes } from "@/components/ClientOutcomes";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.05 } }),
};

const FOR = [
  { icon: Scale, t: "Personal-injury & plaintiff firms", d: "Spend-heavy practices running paid search, TV, billboards, legal directories and lead generators." },
  { icon: Gauge, t: "Multi-vendor marketing programs", d: "Firms juggling several agencies, platforms and lead sources with reporting that never quite reconciles." },
  { icon: ReceiptText, t: "Leadership that wants answers", d: "Managing partners and administrators who need an independent read on what marketing actually produced." },
];

const RECEIVE = [
  "Marketing spend reconciled against vendor invoices and contracts",
  "Vendor-by-vendor performance compared on cost per retained case",
  "Lead-source attribution and lead-to-retained-case analysis",
  "Identification of duplicated, unexplained or hard-to-verify charges",
  "A vendor accountability scorecard",
  "An executive findings report with clear recommendations",
  "An executive review consultation to walk through the results",
];

export default function Customers() {
  const navigate = useNavigate();
  return (
    <div className="dark relative min-h-screen bg-[#0A1B2E] text-[#F5F5F5] font-body overflow-hidden" data-testid="customers-page">
      <Seo title="Who RetainProof is for — independent accountability for law firms" description="RetainProof works with personal-injury and plaintiff law firms running multi-vendor marketing programs, delivering an independent executive read on spend, vendor performance and retained-case outcomes." path="/customers" />
      <div className="grain-overlay" />

      <nav className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="brand-logo">
            <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
            <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/features"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Features</Button></Link>
            <Link to="/pricing"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Pricing</Button></Link>
            <Link to="/about"><Button variant="ghost" className="hidden sm:inline-flex text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">About</Button></Link>
            <Link to="/login"><Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-white/5 rounded-none">Sign in</Button></Link>
            <Button onClick={() => navigate("/register")} className="bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none font-medium">Get started</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-36 pb-14">
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={0} className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-5">Who we work with</motion.p>
        <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1} className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-4xl" data-testid="customers-hero-title">
          Built for law firms that want the <span className="italic text-[#D5A44A]">whole picture.</span>
        </motion.h1>
        <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2} className="mt-6 text-lg text-zinc-400 max-w-3xl leading-relaxed">
          RetainProof is an independent layer between firm leadership and the marketing vendors they rely on. We work from your own invoices, vendor reports, lead records and retained-case data — never on commission, and never selling media.
        </motion.p>
      </section>

      {/* Who it's for */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
          {FOR.map((f) => (
            <div key={f.t} className="border border-white/10 bg-[#12324D] p-7" data-testid="customer-segment">
              <f.icon className="h-6 w-6 text-[#D5A44A]" />
              <p className="font-display text-xl mt-4 leading-tight">{f.t}</p>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What clients receive */}
      <section className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1fr,1.1fr] gap-12 items-start">
          <div>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#0E7C7B] mb-4">What clients receive</p>
            <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight">An independent executive read — not another dashboard.</h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">Every engagement ends with a clear, evidence-based findings report your leadership can act on.</p>
            <Button onClick={() => navigate("/pricing")} className="mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 px-8 text-base font-medium group" data-testid="customers-pricing-cta">
              See assessment options <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
          <ul className="space-y-3 border-l border-white/10 pl-8">
            {RECEIVE.map((r) => (
              <li key={r} className="flex gap-3 text-zinc-300"><Check className="h-4 w-4 text-[#D5A44A] shrink-0 mt-1" />{r}</li>
            ))}
          </ul>
        </div>
      </section>

      <ClientOutcomes />

      {/* Early client note (honest, no fabricated proof) */}
      <section className="relative z-10 border-t border-white/10 bg-[#0B1F33]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <ShieldCheck className="h-8 w-8 text-[#D5A44A] mx-auto" />
          <h2 className="font-display text-3xl font-light tracking-tight mt-4">Engagements are confidential by design.</h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Because our findings touch firms' marketing spend and vendor relationships, RetainProof keeps client engagements private. Firm references and representative findings are shared directly during the consultation and contracting process.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/consultation"><Button variant="outline" className="rounded-none h-12 px-8 text-base border-white/25 text-white bg-transparent hover:bg-white/5" data-testid="customers-consult-cta">Schedule a Consultation</Button></Link>
            <a href="mailto:clientcare@retainproofnow.com"><Button variant="ghost" className="rounded-none h-12 px-8 text-base text-zinc-300 hover:text-white hover:bg-white/5"><FileText className="mr-2 h-4 w-4" /> clientcare@retainproofnow.com</Button></a>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span>© 2026 RetainProof™ · A product of CMD Marketing Enterprises LLC</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/about" className="hover:text-white">About</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
